import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { API, axios } from "@/api";
import { Loader2, X, Globe, Calendar, MapPin, ExternalLink } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import BrandContributionForm from "./BrandContributionForm";

export default function BrandCard({ isOpen, onClose, businessId, businessName, logoUrl }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchBrandData = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/brand-data/${businessId}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (isOpen && businessId) {
      setShowForm(false);
      fetchBrandData();
    }
  }, [isOpen, businessId, fetchBrandData]);

  if (!isOpen) return null;

  const intel = data?.intel;
  const community = data?.community_data;
  const hasData = data?.has_data || !!community;

  const description = intel?.description || community?.description;
  const yearFounded = intel?.year_founded || community?.year_founded;
  const hq = intel?.headquarters;
  const website = intel?.website || community?.website;
  const socials = intel?.social_links || {};
  const igHandle = socials.instagram || community?.instagram;
  const twHandle = socials.twitter || community?.twitter;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300] bg-black/30 backdrop-blur-sm"
          onClick={onClose}
          data-testid="brand-card-overlay"
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 max-h-[80vh] bg-white rounded-t-3xl shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="brand-card-sheet"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-200" />
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
              data-testid="brand-card-close"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
              </div>
            ) : hasData && !showForm ? (
              /* ── STATE A: Brand data exists ── */
              <div className="px-5 pb-8 pt-2" data-testid="brand-card-state-a">
                {/* Brand header */}
                <div className="flex items-center gap-3 mb-4">
                  <BrandLogo
                    name={data?.business_name || businessName}
                    logoUrl={data?.logo_url || logoUrl}
                    size="lg"
                    brandColor={data?.brand_color_primary}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-800 truncate" data-testid="brand-card-name">
                      {data?.business_name || businessName}
                    </h3>
                    {yearFounded && (
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Est. {yearFounded}
                      </p>
                    )}
                  </div>
                </div>

                {/* Description */}
                {description && (
                  <p className="text-sm text-slate-600 leading-relaxed mb-4" data-testid="brand-card-description">
                    {description}
                  </p>
                )}

                {/* Details row */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {hq && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 text-xs text-slate-500 border border-slate-100">
                      <MapPin className="w-3 h-3" /> {hq}
                    </span>
                  )}
                  {intel?.industry && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 text-xs text-slate-500 border border-slate-100">
                      {intel.industry}
                    </span>
                  )}
                </div>

                {/* Links */}
                <div className="space-y-2">
                  {website && (
                    <a
                      href={website.startsWith("http") ? website : `https://${website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-sky-600 hover:text-sky-700"
                      data-testid="brand-card-website"
                    >
                      <Globe className="w-3.5 h-3.5" /> {website.replace(/^https?:\/\//, "")}
                      <ExternalLink className="w-3 h-3 ml-auto" />
                    </a>
                  )}
                  {igHandle && (
                    <a
                      href={`https://instagram.com/${igHandle.replace("@", "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-pink-500"
                      data-testid="brand-card-instagram"
                    >
                      Instagram: @{igHandle.replace("@", "")}
                    </a>
                  )}
                  {twHandle && (
                    <a
                      href={`https://x.com/${twHandle.replace("@", "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-slate-500"
                      data-testid="brand-card-twitter"
                    >
                      X: @{twHandle.replace("@", "")}
                    </a>
                  )}
                </div>

                {/* Improve data CTA */}
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-6 w-full py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500 font-medium"
                  data-testid="brand-card-improve-btn"
                >
                  Know more about this brand? Help improve the data
                </button>
              </div>
            ) : showForm ? (
              /* ── CONTRIBUTION FORM ── */
              <div className="px-5 pb-8 pt-2">
                <BrandContributionForm
                  businessId={businessId}
                  businessName={data?.business_name || businessName}
                  onSuccess={() => { setShowForm(false); fetchBrandData(); }}
                  onCancel={() => setShowForm(false)}
                />
              </div>
            ) : (
              /* ── STATE B: No data — prompt contribution ── */
              <div className="px-5 pb-8 pt-2 text-center" data-testid="brand-card-state-b">
                <BrandLogo
                  name={data?.business_name || businessName}
                  logoUrl={data?.logo_url || logoUrl}
                  size="lg"
                  brandColor={data?.brand_color_primary}
                />
                <h3 className="text-lg font-bold text-slate-800 mt-3" data-testid="brand-card-name">
                  {data?.business_name || businessName}
                </h3>
                <p className="text-sm text-slate-400 mt-2 mb-6">
                  We don't have details about this brand yet.
                  Be the first to contribute!
                </p>
                <button
                  onClick={() => setShowForm(true)}
                  className="px-6 py-2.5 rounded-full bg-amber-500 text-white font-bold text-sm shadow-md"
                  data-testid="brand-card-contribute-btn"
                >
                  Add Brand Info
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
